import { useEffect, useRef, useCallback } from 'react';

/**
 * useSlotVideo — drive one slot's <video> element from the shared playhead (Phase 1e single-cam;
 * called per-slot at the 2a multi-cam sync engine).
 *
 * The playhead (useTimelinePlayhead) is the master clock. The <video> plays at playbackRate = speed
 * so the picture keeps pace between the 1s ticks; we SEEK the element only when (a) the active clip
 * changes (load the new clip's file, then seek to the in-clip offset) or (b) the playhead diverges
 * from the element by more than one tick — a deliberate scrub/rewind/stop, not normal play drift.
 * Seeking every tick would stutter the picture, so the SEEK_THRESHOLD gate avoids it.
 *
 * The element's `src` is owned by the rendering component (Cell sets it from buildStreamUrl); this hook
 * only manages currentTime / play / pause / playbackRate. Pure DOM glue — verified headed (jsdom has no
 * media pipeline), the way the legacy PlayerWindow video handling is.
 *
 * @param {object} videoRef  React ref to the <video> element
 * @param {object} opts
 * @param {number|string|null} opts.clipId  bID of the active clip (null = gap/none) — the seek trigger
 * @param {number} opts.offsetInClip        seconds into the active clip the playhead points at
 * @param {boolean} opts.playing
 * @param {boolean} opts.holding             the shared buffer-gate is holding (a sibling cell is still
 *                                           loading after a seek) — paint the still frame but DON'T play,
 *                                           so every cell starts together when the gate releases
 * @param {number} opts.speed
 * @param {boolean} opts.hasClip            a clip is loaded (false in a gap / empty slot)
 * @param {function} [opts.onBuffering]     2a-3: called with true on a mid-playback underrun
 *                                          (waiting/stalled) and false on recovery (playing/canplay), so
 *                                          the View can feed the master buffer-gate (hold the grid while
 *                                          one tile rebuffers).
 */
const SEEK_THRESHOLD = 1.5; // s — looser than one tick so smooth play never re-seeks

// Decode-error recovery backoff (task 2, David 2026-08-05): reload+reseek+resume after these delays (ms)
// on consecutive media errors, then give up (report 'failed') until the clip changes. Widening so each
// retry reseeks to a later live offset and walks past a bad profile=2 segment; the last slot bounds a
// paused-on-a-bad-clip case (~16s total) instead of spinning forever.
const RETRY_DELAYS = [1000, 5000, 10000];
// How long a clip must keep playing after a recovery before its retry budget is handed back. Shorter
// than the shortest clip, longer than the play-a-frame-then-die loop RETRY_DELAYS is meant to bound.
const SETTLE_MS = 5000;

// play() can reject async (autoplay policy) or throw sync (some engines) — swallow both; the playhead
// stays authoritative and the next state change retries.
const safePlay = (v) => { try { const p = v.play(); if (p && p.catch) p.catch(() => {}); } catch (e) { /* ignore */ } };

export const useSlotVideo = (videoRef, { clipId = null, offsetInClip = 0, playing = false, holding = false, speed = 1, hasClip = false, onBuffering, onRecovery }) => {
  // Latest values for the async loadedmetadata callback (deps may move before it fires).
  const latest = useRef({});
  latest.current = { offsetInClip, playing, holding };

  // Buffering report (2a-3): waiting/stalled/loadstart = mid-playback underrun or a fresh clip loading;
  // playing/canplay/loadeddata = recovered. Kept in a ref so the listener subscribes once (an inline
  // onBuffering would re-subscribe each render). Deduped via bufferingRef so it fires only on TRANSITIONS.
  // CRUCIAL self-heal (David 2026-08-05): `stalled` — and a missed recovery event across a clip advance —
  // can latch buffering=true while the element actually keeps playing from its buffer, with NO paired
  // recovery event ever firing, leaving the spinner stuck over a playing tile. So a `timeupdate` while the
  // picture is genuinely progressing (not paused, readyState>=3) clears it: a moving picture is by
  // definition not buffering. (This is independent of the cog "buffer: ready" line, which reads useVideoInfo.)
  const onBufRef = useRef(onBuffering);
  onBufRef.current = onBuffering;
  const bufferingRef = useRef(false);
  const reportBuffering = useCallback((b) => {
    if (b === bufferingRef.current) return; // only fire on a real transition
    bufferingRef.current = b;
    if (onBufRef.current) onBufRef.current(b);
  }, []);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onWaiting = () => reportBuffering(true);
    const onReady = () => reportBuffering(false);
    const onProgress = () => { if (!v.paused && v.readyState >= 3) reportBuffering(false); };
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('stalled', onWaiting);
    v.addEventListener('loadstart', onWaiting);  // a fresh clip started loading → buffering
    v.addEventListener('playing', onReady);
    v.addEventListener('canplay', onReady);
    v.addEventListener('loadeddata', onReady);   // first frame decoded → ready
    v.addEventListener('timeupdate', onProgress); // self-heal: a moving picture is never "buffering"
    return () => {
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('stalled', onWaiting);
      v.removeEventListener('loadstart', onWaiting);
      v.removeEventListener('playing', onReady);
      v.removeEventListener('canplay', onReady);
      v.removeEventListener('loadeddata', onReady);
      v.removeEventListener('timeupdate', onProgress);
    };
  }, [videoRef, reportBuffering]);

  // Decode/media-error recovery (task 2, David 2026-08-05). A profile=2 transcoded clip can throw
  // MediaError code 3 (decode) mid-clip even though the file serves HTTP 200 — the <video> halts on its
  // last frame and, if the bad clip is long, stays frozen until the playhead crosses into the next clip.
  // Recover in place: reload + re-seek to the LIVE offset (the master playhead has kept advancing, so each
  // retry lands further along and walks past the bad segment) + resume, on a widening backoff — so the
  // tile SELF-HEALS without ever staying permanently dead, and WITHOUT falling back to profile=1 (many
  // full-res cams overwhelm older backends: RC01 can't stream >2 reliably — David). onRecovery reports
  // 'recovering' while a reload is pending, 'ok' on success, and 'failed' once the backoff is exhausted
  // (the cell surfaces "Can't play this clip" until the playhead moves on). Reset on a clip change / play.
  const onRecoveryRef = useRef(onRecovery);
  onRecoveryRef.current = onRecovery;
  const reportRecovery = useCallback((s) => { if (onRecoveryRef.current) onRecoveryRef.current(s); }, []);
  const retryRef = useRef({ n: 0, timer: null });
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const clearTimer = () => { if (retryRef.current.timer) { clearTimeout(retryRef.current.timer); retryRef.current.timer = null; } };
    const clearSettle = () => { if (retryRef.current.settle) { clearTimeout(retryRef.current.settle); retryRef.current.settle = null; } };
    const recover = () => {
      const vv = videoRef.current;
      if (!vv) return;
      const { offsetInClip: off, playing: play, holding: hold } = latest.current;
      try { vv.load(); } catch (e) { /* jsdom / no media pipeline */ }
      const seekResume = () => {
        try { vv.currentTime = off; } catch (e) { /* not seekable yet */ }
        if (play && !hold) safePlay(vv);
      };
      if (vv.readyState >= 1) seekResume();
      else vv.addEventListener('loadedmetadata', seekResume, { once: true });
    };
    const onError = () => {
      const r = retryRef.current;
      clearSettle(); // playback did not survive long enough to count as recovered — keep spending budget
      if (r.n >= RETRY_DELAYS.length) {   // backoff exhausted — surface it + drop the gate hold for a dead tile
        reportRecovery('failed');
        reportBuffering(false);
        return;
      }
      const delay = RETRY_DELAYS[r.n];
      r.n += 1;
      reportRecovery('recovering');
      clearTimer();
      retryRef.current.timer = setTimeout(recover, delay);
    };
    // A `playing` event alone is NOT proof of recovery. A profile=2 clip that decodes a frame or two and
    // then throws MediaError 3 fires playing -> error -> playing -> error, and resetting the budget on
    // each `playing` meant the documented "give up after ~16s" bound never applied: the tile reloaded
    // roughly once a second for as long as the playhead sat on that clip. Require SUSTAINED clean
    // playback before handing the budget back, and cancel that timer the moment another error lands.
    // #86bb7phhh
    const onOk = () => {
      clearTimer();
      reportRecovery('ok');
      clearSettle();
      retryRef.current.settle = setTimeout(() => { retryRef.current.n = 0; retryRef.current.settle = null; }, SETTLE_MS);
    };
    v.addEventListener('error', onError);
    v.addEventListener('playing', onOk);
    return () => { v.removeEventListener('error', onError); v.removeEventListener('playing', onOk); clearTimer(); clearSettle(); };
  }, [videoRef, reportRecovery, reportBuffering]);

  // A fresh clip = a clean retry budget (and cancels any pending reload from the previous clip).
  useEffect(() => {
    retryRef.current.n = 0;
    if (retryRef.current.timer) { clearTimeout(retryRef.current.timer); retryRef.current.timer = null; }
    if (retryRef.current.settle) { clearTimeout(retryRef.current.settle); retryRef.current.settle = null; }
    reportRecovery('ok');
  }, [clipId, reportRecovery]);

  // playbackRate follows speed (re-applied on clip change since a fresh element resets it to 1).
  useEffect(() => {
    const v = videoRef.current;
    if (v && v.playbackRate !== speed) v.playbackRate = speed;
  }, [videoRef, speed, clipId]);

  // Clip change: seek to the in-clip offset once metadata is available, then honor play state.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !hasClip) return undefined;
    // A fresh clip is loading until its first frame decodes (readyState >= 2 = HAVE_CURRENT_DATA).
    // Report buffering NOW so the multi-cam buffer-gate holds ALL cams until each has its first
    // frame — otherwise a cam that decodes first races ahead while a slower sibling still spins.
    if (v.readyState < 2) reportBuffering(true);
    const seekThenPlay = () => {
      const { offsetInClip: off, playing: play, holding: hold } = latest.current;
      try { v.currentTime = off; } catch (e) { /* not seekable yet */ }
      // Setting currentTime paints the frame; only PLAY once the shared gate has released (all cells
      // ready) — so a freshly-loaded clip after a seek waits for its siblings instead of racing ahead.
      if (play && !hold) safePlay(v);
    };
    if (v.readyState >= 1) { seekThenPlay(); return undefined; }
    v.addEventListener('loadedmetadata', seekThenPlay, { once: true });
    return () => v.removeEventListener('loadedmetadata', seekThenPlay);
  }, [videoRef, clipId, hasClip, reportBuffering]); // reportBuffering is a stable useCallback([])

  // Play / pause follows the shared playing state on the current clip — GATED by the buffer-gate: while
  // it holds (a sibling still loading after a seek) every cell stays paused on its first painted frame,
  // then this effect re-runs when `holding` clears and they all play together (in-sync, Java MCBufferGate).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing && !holding && hasClip) safePlay(v);
    else { try { v.pause(); } catch (e) { /* not ready */ } }
  }, [videoRef, playing, holding, hasClip]);

  // Scrub / rewind / stop: when the playhead jumps away from the element (> one tick), re-seek to match.
  // Normal play keeps |Δ| < 1s so this never fires mid-playback.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !hasClip) return;
    if (Math.abs(v.currentTime - offsetInClip) > SEEK_THRESHOLD) {
      try { v.currentTime = offsetInClip; } catch (e) { /* not seekable yet */ }
    }
  }, [videoRef, offsetInClip, hasClip]);
};

export default useSlotVideo;
